import { useEffect, useState } from 'react';

import { CountryConfig } from '../IncidentMap';
import { withBase } from '../MapSharedData';
import { countries } from '../constants/countryData';
import { PostcodeIndex, loadCountryPostcodes } from '../parseGeonames';

interface UseUnifiedCountryDataResult {
  geojson: GeoJSON.FeatureCollection | null;
  postcodes: PostcodeIndex | null;
  config: CountryConfig | null;
  error: string | null;
}

const INITIAL: UseUnifiedCountryDataResult = {
  geojson: null,
  postcodes: null,
  config: null,
  error: null,
};

/**
 * Parsed region GeoJSON cached per URL at module scope, so leaving a country
 * for the world view and re-entering the same one reuses the fetch + parse
 * instead of hitting the network and re-parsing a (potentially large) file.
 * Mirrors loadCountryPostcodes' promise cache. Rejected fetches are evicted so
 * a transient failure can be retried on the next entry.
 */
const geoJsonCache = new Map<string, Promise<GeoJSON.FeatureCollection>>();

function loadCountryGeoJson(url: string): Promise<GeoJSON.FeatureCollection> {
  let p = geoJsonCache.get(url);
  if (!p) {
    p = fetch(withBase(url)).then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status} loading ${url}`);
      return res.json() as Promise<GeoJSON.FeatureCollection>;
    });
    p.catch(() => geoJsonCache.delete(url));
    geoJsonCache.set(url, p);
  }
  return p;
}

/**
 * Loads the active country's region polygons + GeoNames postcode index for
 * the persistent UnifiedMap (REFACTOR_PLAN.md). Mirrors the legacy
 * useCountryGeoJson semantics but tolerates a null activeCountry — when the
 * user is on the world view, the hook returns the initial all-null state
 * instead of triggering a fetch with empty values.
 *
 * loadCountryPostcodes caches internally so re-selecting a country is cheap.
 */
export function useUnifiedCountryData(activeCountry: string | null): UseUnifiedCountryDataResult {
  const [state, setState] = useState<UseUnifiedCountryDataResult>(INITIAL);

  useEffect(() => {
    if (!activeCountry) {
      setState(INITIAL);
      return;
    }
    const config = countries[activeCountry];
    if (!config) {
      // Country has incidents but no drill-down config — drilling in is a
      // no-op in IncidentMap, so we don't fetch anything here.
      setState({ ...INITIAL, config: null });
      return;
    }

    // Reset state immediately so downstream hooks (useUnifiedAggregation,
    // useUnifiedDrillIn) don't briefly see the previous country's geojson
    // while the new fetch is in flight.
    setState({ geojson: null, postcodes: null, config, error: null });

    let cancelled = false;
    (async () => {
      try {
        const [geojsonRes, postcodes] = await Promise.all([
          loadCountryGeoJson(config.geoJsonUrl),
          loadCountryPostcodes(activeCountry),
        ]);
        if (cancelled) return;
        if (!geojsonRes.features?.length) throw new Error('Empty GeoJSON');
        setState({ geojson: geojsonRes, postcodes, config, error: null });
      } catch (err) {
        if (cancelled) return;
        setState({
          geojson: null,
          postcodes: null,
          config,
          error: `Failed to load map: ${err instanceof Error ? err.message : 'Unknown'}`,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeCountry]);

  return state;
}
